import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Area, AreaChart } from 'recharts';
import { format } from 'date-fns';

export interface ChartDataPoint {
  timestamp: number;
  [key: string]: number | string;
}

interface MetricsChartProps {
  data: ChartDataPoint[];
  lines: Array<{
    key: string;
    name: string;
    color: string;
  }>;
  yAxisLabel?: string;
  valueFormatter?: (value: number) => string;
  showArea?: boolean;
}

export function MetricsChart({
  data,
  lines,
  yAxisLabel,
  valueFormatter = (v) => v.toFixed(2),
  showArea = false,
}: MetricsChartProps) {
  const formatTimestamp = (timestamp: number) => {
    return format(new Date(timestamp * 1000), 'HH:mm');
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-lg p-3">
          <p className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-2">
            {format(new Date(Number(label) * 1000), 'PPpp')}
          </p>
          {payload.map((entry: any, index: number) => (
            <div key={index} className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-sm text-slate-600 dark:text-slate-400">{entry.name}</span>
              </div>
              <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                {valueFormatter(entry.value)}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  if (showArea && lines.length === 1) {
    return (
      <ResponsiveContainer width="100%" height={320}>
        <AreaChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
          <defs>
            <linearGradient id={`gradient-${lines[0].key}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={lines[0].color} stopOpacity={0.3} />
              <stop offset="95%" stopColor={lines[0].color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" className="dark:stroke-slate-800" />
          <XAxis
            dataKey="timestamp"
            tickFormatter={formatTimestamp}
            tick={{ fill: '#64748b', fontSize: 12 }}
            axisLine={{ stroke: '#e2e8f0' }}
            className="dark:text-slate-400"
          />
          <YAxis
            label={yAxisLabel ? { value: yAxisLabel, angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#64748b' } } : undefined}
            tick={{ fill: '#64748b', fontSize: 12 }}
            axisLine={{ stroke: '#e2e8f0' }}
            className="dark:text-slate-400"
          />
          <Tooltip content={<CustomTooltip />} />
          <Area
            type="monotone"
            dataKey={lines[0].key}
            stroke={lines[0].color}
            strokeWidth={2.5}
            fill={`url(#gradient-${lines[0].key})`}
            name={lines[0].name}
          />
        </AreaChart>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={320}>
      <LineChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" className="dark:stroke-slate-800" />
        <XAxis
          dataKey="timestamp"
          tickFormatter={formatTimestamp}
          tick={{ fill: '#64748b', fontSize: 12 }}
          axisLine={{ stroke: '#e2e8f0' }}
          className="dark:text-slate-400"
        />
        <YAxis
          label={yAxisLabel ? { value: yAxisLabel, angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#64748b' } } : undefined}
          tick={{ fill: '#64748b', fontSize: 12 }}
          axisLine={{ stroke: '#e2e8f0' }}
          className="dark:text-slate-400"
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend
          wrapperStyle={{ paddingTop: '20px' }}
          iconType="line"
          formatter={(value) => <span className="text-sm text-slate-600 dark:text-slate-400">{value}</span>}
        />
        {lines.map((line) => (
          <Line
            key={line.key}
            type="monotone"
            dataKey={line.key}
            name={line.name}
            stroke={line.color}
            strokeWidth={2.5}
            dot={false}
            activeDot={{ r: 5, fill: line.color, strokeWidth: 2, stroke: '#fff' }}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

